import { compact } from 'lodash';
import * as moment from 'moment';

import {
  InspectionRecurrencePeriod,
  InspectionStatus,
} from '@main-application/inspections/models/rest-inspections-model.interface';
import { GetRecurrencePeriodTypeList } from '@shared/constants/recurrence-period-type-list.const';
import { paramsCompare } from '@shared/functions/params-compare.function';
import { ParamsCompareFunc, ParamsCompareItem } from '@shared/interfaces/params-compare-item';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';

import { InspectionsListConstants } from '../../../components/inspections-list/constants';
import { DynamicInspectionModel } from '../../../models/dynamic-inspection.model';
import { InspectionModel } from '../../../models/inspection.model';
import { IInspectionFilter } from '../interfaces/filter.interface';

export function compareElementWithFilterParams(
  filterItem: IInspectionFilter,
  element: InspectionModel,
  inspectionTemplates: RestTemplateModel[]
): (ParamsCompareItem | ParamsCompareFunc)[] {
  const compareParams: (ParamsCompareItem | ParamsCompareFunc)[] = [];

  if (filterItem?.unitOrAssignee) {
    const template = inspectionTemplates.find(x => x.id === element?.entityDetails?.inspectionTemplateId);
    const compareWith = filterItem?.unitOrAssignee?.toLowerCase();
    compareWith &&
      compareParams.push({
        currentValue: compact([
          element.entityDetails?.unit?.name ?? '',
          element.entityDetails?.unit?.propertyName ?? '',
          element.entityDetails?.assigneeUser?.displayName ?? 'unassigned',
          element.entityDetails?.unit?.unitType ?? '',
          template?.name ?? '',
        ]).some(x => x.toLowerCase().includes(compareWith)),
        expectValue: true,
      });
  }

  if (filterItem?.propertyId != null) {
    compareParams.push({
      currentValue: element.propertyId,
      expectValue: filterItem?.propertyId,
    });
  }

  if (filterItem?.status?.length) {
    const hasLateStatus = filterItem.status.includes(InspectionStatus.Late);
    const nonCompletedStatus = [InspectionStatus.NotStarted, InspectionStatus.InProgress];
    compareParams.push({
      currentValue:
        (hasLateStatus &&
          nonCompletedStatus.includes(element.entityDetails.status) &&
          moment(element.entityDetails.dueDate).isBefore(moment(), 'day')) ||
        filterItem.status.includes(element.entityDetails.status) ||
        element.recurrencePeriod > 0,
      expectValue: true,
    });
  }

  if (filterItem?.assigneeId?.length) {
    compareParams.push({
      currentValue: filterItem.assigneeId.includes(element.entityDetails.assignee || -1),
      expectValue: true,
    });
  }

  if (filterItem?.templateIds?.length) {
    compareParams.push({
      currentValue: filterItem.templateIds.includes(element.entityDetails.inspectionTemplateId),
      expectValue: true,
    });
  }

  if (
    Array.isArray(filterItem?.recurrencePeriod) &&
    filterItem.recurrencePeriod.length &&
    element.recurrencePeriod !== null &&
    element.entityDetails?.dueDate
  ) {
    const options = GetRecurrencePeriodTypeList(new Date(element.entityDetails.dueDate));
    const currentOption = options.find(opt => opt.value === element.recurrencePeriod);

    const matchFound = filterItem.recurrencePeriod.some(selected => {
      if (typeof selected === 'string') {
        return selected === currentOption?.label;
      }
      if (typeof selected === 'number') {
        const generalize = (value: InspectionRecurrencePeriod): InspectionRecurrencePeriod => {
          switch (value) {
            case InspectionRecurrencePeriod.SameWeekMonthly:
              return InspectionRecurrencePeriod.Monthly;
            case InspectionRecurrencePeriod.SameWeekQuarterly:
              return InspectionRecurrencePeriod.Quarterly;
            default:
              return value;
          }
        };
        return selected === generalize(element.recurrencePeriod);
      }

      return false;
    });

    compareParams.push({
      currentValue: matchFound,
      expectValue: true,
    });
  }

  // if (isExist(filterItem?.isBulk)) {
  //   compareParams.push({
  //     currentValue: element.entityDetails.isBulk,
  //     expectValue: filterItem.isBulk,
  //   });
  // }

  return compareParams;
}

export function dynamicTableFilterPredicate(
  element: DynamicInspectionModel,
  filter: string | null | undefined
): boolean {
  if (filter == null) {
    return true;
  }

  const filterItem: IInspectionFilter = JSON.parse(filter);
  const compareParams: (ParamsCompareItem | ParamsCompareFunc)[] = [];

  if (filterItem?.unitOrAssignee) {
    const compareWith = filterItem.unitOrAssignee.toLowerCase();
    compareParams.push({
      currentValue: compact([
        element.propertyName,
        element?.assigneeUser?.displayName ?? 'unassigned',
        element.step,
        element.notify,
        element.due,
        element.inspectionType,
      ]).some(x => x.toLowerCase().includes(compareWith)),
      expectValue: true,
    });
  }

  if (filterItem?.propertyIds?.length) {
    compareParams.push({
      currentValue: element.propertyId === -1 || filterItem.propertyIds.includes(element.propertyId),
      expectValue: true,
    });
  }

  if (filterItem?.portfolioId != null) {
    compareParams.push({
      currentValue: element.portfolioId,
      expectValue: filterItem?.portfolioId,
    });
  }

  if (filterItem?.assigneeId?.length) {
    compareParams.push({
      currentValue: filterItem.assigneeId.includes(element.entityDetails.actualAssigneeId),
      expectValue: true,
    });
  }

  if (filterItem?.templateIds?.length) {
    compareParams.push({
      currentValue: filterItem.templateIds.includes(element.entityDetails.inspectionTemplateId),
      expectValue: true,
    });
  }

  return paramsCompare(compareParams);
}

export function getTableFilterPredicate(inspectionTemplates: RestTemplateModel[]) {
  return function (element: InspectionModel, filter: string | null | undefined): boolean {
    if (filter == null) {
      return true;
    }

    const filterItem: IInspectionFilter = JSON.parse(filter);

    const comparedParams = compareElementWithFilterParams(filterItem, element, inspectionTemplates);

    return paramsCompare(comparedParams);
  };
}

export function getFilterDateRange(
  today: Date,
  //timeFrame?: InspectionsListConstants.TimeFrame,
  filter: { [key: string]: any },
  formattedString = false
): { startDate: Date | string; endDate?: Date | string } {
  const timeFrame = filter.timeframe;
  const formatDate = (date: moment.Moment) => (formattedString ? date.format('YYYY-MM-DD') : date.toDate());

  if (!timeFrame) {
    return { startDate: formatDate(moment({ year: 2022, month: 0, day: 1 })) };
  } else {
    switch (timeFrame) {
      case InspectionsListConstants.TimeFrame.Week:
        return { startDate: formatDate(moment(today).subtract(1, 'week')) };
      case InspectionsListConstants.TimeFrame.Month:
        return { startDate: formatDate(moment(today).subtract(1, 'month')) };
      case InspectionsListConstants.TimeFrame.Month_3:
        return { startDate: formatDate(moment(today).subtract(3, 'months')) };
      case InspectionsListConstants.TimeFrame.Month_6:
        return { startDate: formatDate(moment(today).subtract(6, 'months')) };
      case InspectionsListConstants.TimeFrame.Year:
        return { startDate: formatDate(moment(today).subtract(1, 'year')) };
      case InspectionsListConstants.TimeFrame.AllTime:
        return { startDate: formatDate(moment({ year: 2022, month: 0, day: 1 })) };
      case InspectionsListConstants.TimeFrame.Custom:
        return {
          startDate: formattedString ? moment(filter.startDate).format('YYYY-MM-DD') : filter.startDate,
          endDate: formattedString ? moment(filter.endDate).format('YYYY-MM-DD') : filter.endDate,
        };
      case InspectionsListConstants.TimeFrame.ThisWeek:
        return {
          startDate: formatDate(moment(today).startOf('week')),
          endDate: formatDate(moment(today).endOf('week')),
        };
      case InspectionsListConstants.TimeFrame.NextTwoWeeks:
        return {
          startDate: formatDate(moment(today)),
          endDate: formatDate(moment(today).add(2, 'weeks')),
        };
      case InspectionsListConstants.TimeFrame.ThisMonth:
        return {
          startDate: formatDate(moment(today).startOf('month')),
          endDate: formatDate(moment(today).endOf('month')),
        };
      case InspectionsListConstants.TimeFrame.ThisQuarter:
        return {
          startDate: formatDate(moment(today).startOf('quarter')),
          endDate: formatDate(moment(today).endOf('quarter')),
        };
      case InspectionsListConstants.TimeFrame.ThisYear:
        return {
          startDate: formatDate(moment(today).startOf('year')),
          endDate: formatDate(moment(today).endOf('year')),
        };
    }
  }
}
